import React from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer, Tooltip } from 'recharts';

export default function AthleteRadarChart({ 
  athletes, // Array of athlete objects with attributes
  attributes, // Array of attribute names to display
  title,
  colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']
}) {
  if (!athletes || athletes.length === 0 || !attributes || attributes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        No athlete data available
      </div>
    );
  }

  // Normalize data to 0-100 scale for each attribute
  const normalizedData = normalizeAthleteData(athletes, attributes);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-lg">
          <p className="text-slate-100 font-semibold mb-2">{payload[0].payload.attribute}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-slate-300 text-sm">
              <span className="font-semibold" style={{ color: entry.stroke }}>
                {entry.name}:
              </span> {entry.value.toFixed(1)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full">
      {title && (
        <h3 className="text-lg font-semibold text-slate-100 mb-4">{title}</h3>
      )}
      
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={normalizedData}>
          <PolarGrid stroke="#334155" />
          <PolarAngleAxis 
            dataKey="attribute" 
            tick={{ fill: '#94a3b8', fontSize: 12 }}
          />
          <PolarRadiusAxis 
            angle={90} 
            domain={[0, 100]}
            tick={{ fill: '#94a3b8' }}
            stroke="#64748b"
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="circle"
          />
          
          {athletes.map((athlete, index) => (
            <Radar
              key={athlete.id || athlete.name || index}
              name={athlete.name || `Athlete ${index + 1}`}
              dataKey={`athlete_${index}`}
              stroke={colors[index % colors.length]}
              fill={colors[index % colors.length]}
              fillOpacity={0.2}
              strokeWidth={2}
            />
          ))}
        </RadarChart>
      </ResponsiveContainer>

      {/* Athlete legend with stats */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {athletes.map((athlete, index) => (
          <div 
            key={athlete.id || athlete.name || index}
            className="bg-slate-800/50 rounded-lg p-3 border border-slate-700"
          >
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: colors[index % colors.length] }}
              ></div>
              <span className="text-sm font-semibold text-slate-100">
                {athlete.name || `Athlete ${index + 1}`}
              </span>
            </div>
            <div className="text-xs text-slate-400 space-y-1">
              {attributes.slice(0, 3).map(attr => {
                const value = athlete[attr];
                const numValue = typeof value === 'number' ? value : parseFloat(value);
                return (
                  <div key={attr} className="flex justify-between">
                    <span>{formatAttribute(attr)}:</span>
                    <span className="text-slate-300">{!isNaN(numValue) ? numValue.toFixed(1) : 'N/A'}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Normalize athlete data to 0-100 scale
function normalizeAthleteData(athletes, attributes) {
  // Find min/max for each attribute across all athletes
  const ranges = {};
  attributes.forEach(attr => {
    const values = athletes.map(a => a[attr]).filter(v => v != null);
    ranges[attr] = {
      min: Math.min(...values),
      max: Math.max(...values)
    };
  });

  // Create normalized data structure
  return attributes.map(attr => {
    const dataPoint = { attribute: formatAttribute(attr) };
    
    athletes.forEach((athlete, index) => {
      const value = athlete[attr];
      if (value != null) {
        const range = ranges[attr];
        const normalized = range.max > range.min 
          ? ((value - range.min) / (range.max - range.min)) * 100
          : 50; // Default to middle if no range
        dataPoint[`athlete_${index}`] = normalized;
      } else {
        dataPoint[`athlete_${index}`] = 0;
      }
    });
    
    return dataPoint;
  });
}

// Format attribute name for display
function formatAttribute(attr) {
  return attr
    .replace(/_/g, ' ')
    .replace(/cm|sec|kg/g, '')
    .trim()
    .replace(/\b\w/g, l => l.toUpperCase());
}
